import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { LazyMotion, MotionConfig } from "motion/react";
import { useState, type ReactNode } from "react";
import { RouterProvider } from "react-router";

import { ApiError } from "../api/client";
import { ToastProvider } from "../ui/Overlays";
import { createAppRouter } from "./router";

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: (count, error) =>
          count < 2 && !(error instanceof ApiError && error.status >= 400 && error.status < 500),
      },
      mutations: { retry: false },
    },
  });
}

// Motion's feature bundle loads asynchronously, so it stays off the critical path; `strict`
// turns any full `motion.*` component (which would pull it back in) into an error.
const loadMotionFeatures = () => import("./motionFeatures").then((module) => module.default);

/** Providers shared by the app and by component tests. */
export function AppProviders({ client, children }: { client: QueryClient; children: ReactNode }) {
  return (
    <QueryClientProvider client={client}>
      <MotionConfig reducedMotion="user">
        <LazyMotion features={loadMotionFeatures} strict>
          <ToastProvider>{children}</ToastProvider>
        </LazyMotion>
      </MotionConfig>
    </QueryClientProvider>
  );
}

export function App() {
  const [client] = useState(createQueryClient);
  const [router] = useState(createAppRouter);
  return (
    <AppProviders client={client}>
      <RouterProvider router={router} />
    </AppProviders>
  );
}
